import React, { Suspense, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { VIDEO_PLAYER_CLASS } from '~/common/id.constants';
import LoomVideo from '~/common/VideoPlayer/LoomVideo';
import HBLoader from '@honeybook/hbui/dist/components/HBLoader/HBLoader';
import styled from 'styled-components';
import { PicTimeVideo } from '~/common/VideoPlayer/PicTimeVideo';
import {
    IframeStreamingPlatform,
    StreamingPlatform
} from '~/components/Builder/Block/VideoBlock/VideoBlock.types';
const ReactPlayer = React.lazy(() => import('react-player'));

interface Props {
    isAutoplayOn: boolean;
    isLoopOn: boolean;
    isMuted?: boolean;
    isOnline: boolean;
    isVideoControlsOn?: boolean;
    backgroundPosition?: 'center' | 'top' | 'bottom' | 'left' | 'right';
    width?: string;
    height?: string;
    loaderColor?: string;
    setHasPlayerError?: (state: boolean) => void;
    videoPosterUrl?: string;
    videoLink: string;
    streamingPlatform?: StreamingPlatform;
    isTransparentBackgroundOn?: boolean;
}
type IframeVideoComponent = typeof PicTimeVideo | typeof LoomVideo;

export const IFRAME_VIDEO_MAP: Record<
    IframeStreamingPlatform,
    IframeVideoComponent
> = {
    Loom: LoomVideo,
    PicTime: PicTimeVideo
};

const VideoPlayer = ({
    streamingPlatform,
    backgroundPosition = 'center',
    width,
    height,
    loaderColor,
    videoPosterUrl,
    setHasPlayerError,
    videoLink,
    ...videoConfig
}: Props) => {
    const [isReady, setIsReady] = useState(false);

    const onReady = useCallback(() => {
        setIsReady(true);
        setHasPlayerError?.(false);
    }, [setHasPlayerError]);

    const onError = useCallback(
        () => setHasPlayerError(true),
        [setHasPlayerError]
    );

    const IframeVideoComponent: IframeVideoComponent =
        IFRAME_VIDEO_MAP[streamingPlatform];

    if (IframeVideoComponent) {
        return (
            <IframeVideoComponent
                onError={onError}
                onReady={onReady}
                videoLink={videoLink}
                {...videoConfig}
            />
        );
    }

    const { isAutoplayOn, isLoopOn, isMuted, isOnline, isVideoControlsOn } =
        videoConfig;

    return (
        <>
            {!isReady && !videoPosterUrl && (
                <HBLoader size="medium" color={loaderColor} />
            )}

            <Suspense fallback={<></>}>
                <PlayerWrapper
                    isReady={isReady}
                    posterUrl={videoPosterUrl}
                    backgroundPosition={backgroundPosition}
                >
                    <ReactPlayer
                        className={VIDEO_PLAYER_CLASS}
                        controls={isVideoControlsOn}
                        height={height || '100%'}
                        width={width || '100%'}
                        key={String(isVideoControlsOn)} // this is so that react-player would remount when changing this setting
                        loop={isLoopOn}
                        muted={isMuted}
                        onError={onError}
                        onReady={onReady}
                        playing={isAutoplayOn}
                        playsinline={isAutoplayOn || undefined}
                        url={isOnline ? videoLink : null}
                        volume={isAutoplayOn ? 0 : undefined} // volume has to be set for mute + autoplay to work https://github.com/cookpete/react-player/issues/357
                    />
                </PlayerWrapper>
            </Suspense>
        </>
    );
};

export default observer(VideoPlayer);

export const PlayerWrapper = styled.div<{
    isReady: boolean;
    posterUrl?: string;
    backgroundPosition: string;
    width?: string;
    height?: string;
}>`
    background-image: ${({ posterUrl, isReady }) =>
        !!posterUrl && !isReady ? `url(${posterUrl})` : 'none'};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: ${({ backgroundPosition }) => backgroundPosition};
    position: relative;
    justify-content: center;
    display: flex;
    width: ${({ width }) => (!!width ? width : '100%')};
    height: ${({ height }) => (!!height ? height : '100%')};
`;
